/**
 * Iter XX.272 — Gully Challenge #001 · API client.
 *
 * Thin wrapper over the 5 backend endpoints. Every call is safe
 * against network failure — the caller decides what to render on
 * an unavailable challenge (usually: nothing). Anonymous-safe.
 */
import api from "./api";

const CHALLENGE_ORIGIN_KEY = "gully_challenge_origin_v1";

/**
 * Fetch the current challenge state — anonymous-safe. Returns:
 *   { available: false, reason }               when hidden
 *   { available: true, challenge_id, title, participant_count,
 *     user_status, user_focus, user_access_type, days_elapsed,
 *     total_days, ... }                        when exposed
 */
export async function fetchCurrentChallenge() {
  try {
    const { data } = await api.get("/challenges/current");
    return data || { available: false };
  } catch {
    return { available: false };
  }
}

export async function joinChallenge(challengeId) {
  try {
    const { data } = await api.post(`/challenges/${challengeId}/join`);
    return { ok: true, ...data };
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 402) {
      return { ok: false, requires_plus: true, detail };
    }
    return { ok: false, status, detail };
  }
}

export async function setChallengeFocus(challengeId, { focusArea, focusText,
                                                          sessionId }) {
  try {
    const { data } = await api.post(`/challenges/${challengeId}/focus`, {
      focus_area: focusArea,
      focus_text: focusText,
      session_id: sessionId || null,
    });
    return { ok: true, ...data };
  } catch (e) {
    return { ok: false, detail: e?.response?.data?.detail };
  }
}

export async function shareChallengeProgress(challengeId, { title, body }) {
  try {
    const { data } = await api.post(
      `/challenges/${challengeId}/share_progress`,
      { title, body: body || null },
    );
    return { ok: true, ...data };
  } catch (e) {
    return { ok: false, detail: e?.response?.data?.detail };
  }
}

export async function fetchChallengeFeed(challengeId, limit = 30) {
  try {
    const { data } = await api.get(
      `/challenges/${challengeId}/feed`, { params: { limit } },
    );
    return data?.items || [];
  } catch {
    return [];
  }
}

// -------- purchase-origin roundtrip -----------------------------------

/**
 * Stash the challenge origin BEFORE a Plus purchase kicks off, so
 * the /upgrade success handler can route the user back to the
 * challenge instead of generic Home. Reuses localStorage — the
 * same mechanism `beginPlusPurchase` uses for `entryPoint`.
 */
export function stashChallengeOrigin(challengeId) {
  try {
    localStorage.setItem(
      CHALLENGE_ORIGIN_KEY,
      JSON.stringify({ challenge_id: challengeId, at: Date.now() }),
    );
  } catch { /* noop */ }
}

/**
 * Read + clear the stashed challenge origin. Returns { challenge_id }
 * when present, otherwise null. Called ONCE from /upgrade on
 * successful purchase so subsequent visits don't loop.
 */
export function consumeChallengeOrigin() {
  try {
    const raw = localStorage.getItem(CHALLENGE_ORIGIN_KEY);
    if (!raw) return null;
    localStorage.removeItem(CHALLENGE_ORIGIN_KEY);
    const p = JSON.parse(raw);
    if (!p || !p.challenge_id) return null;
    // Expire after 24h so a stale stash doesn't hijack unrelated
    // future purchases.
    if (typeof p.at === "number" && Date.now() - p.at > 24 * 3600 * 1000) {
      return null;
    }
    return { challenge_id: p.challenge_id };
  } catch {
    return null;
  }
}

/**
 * Read-only variant — for the paywall/checkout gate to know
 * whether it should fire `challenge_plus_paywall_view` and later
 * `challenge_plus_purchase_success` correlated with the same
 * challenge_id. Does NOT clear the stash.
 */
export function peekChallengeOrigin() {
  try {
    const raw = localStorage.getItem(CHALLENGE_ORIGIN_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.challenge_id) return null;
    return { challenge_id: p.challenge_id };
  } catch {
    return null;
  }
}

// -------- Home banner helper -----------------------------------------

/**
 * Should the small Home banner render for THIS user's state?
 *
 * Per founder rule: don't turn Home into an ad. Show only when
 * contextually useful:
 *   (a) user is invited but hasn't joined yet
 *   (b) user is participating in an active challenge
 *   (c) user is a Plus subscriber and registration is still open
 *
 * Everyone else: hide. Pulse remains the primary discovery surface.
 */
export function shouldShowHomeChallengeBanner(state, { isPlusActive } = {}) {
  if (!state || state.available === false) return false;
  const status = state.user_status;
  if (status === "invited") return true;
  if (status === "participating") return true;
  if (status === "completed") return false;
  // not_joined branch — only for Plus users while registration is open.
  if (!isPlusActive) return false;
  const closesAt = state.registration_closes_at;
  if (!closesAt) return false;
  try {
    return Date.now() < Date.parse(closesAt);
  } catch {
    return false;
  }
}
